import { useCallback, useState } from 'react';

import { MissingHealthcareProviderActiveP1CertificatesFault } from '@dreryk/egabinet-p1-frontend-api';
import {
   ElectronicReferralApprovalResult,
   P1HandshakeFault,
   ReferralVerificationFault,
} from '@dreryk/egabinet-referral-frontend-api';
import {
   InvalidPrivateKeyFault,
   NoValidCertificateFoundFault,
   PrivateKeyNotDecodedFault,
} from '@dreryk/egabinet-truststore-published-language-api';
import { VisitReferralToSpecialistApproveCommand } from '@dreryk/egabinet-visit-frontend-api';

import { useCqrsCommand, useDataSyncing } from '@dreryk/command-query-react-common';
import {
   ActionBar,
   ButtonVariant,
   DataSyncWaitingEventInterceptor,
   dispatchSnackbarNegativeEvent,
   usePushBack,
   ValidateFormEventInterceptor,
} from '@dreryk/react-components';
import { useDraftRemoveByTokenCqrsCommand } from '@dreryk/react-draft';

import { useAccountAuthorizationModal, useCertificateFaultHandlerMap } from '~libs.desktop.shared.account';
import { visitPaths } from '~libs.desktop.shared.paths';
import { PatientNfzDepartmentUpdateShowModalEventInterceptor } from '~libs.desktop.shared.patient';

import { getReferralDraftToken } from '../../referral.draft';
import {
   dispatchElectronicReferralCodeSnackbarPositiveEvent,
   getReferralVerificationResultErrorList,
} from '../../shared/common.utils';
import { ElectronicReferralFailureModal } from '../../shared/ElectronicReferralFailureModal';
import { referralTranslationKeys } from '../../translations';
import {
   ReferralToSpecialistFormBean,
   ReferralToSpecialistFormController,
} from '../ReferralToSpecialistForm/ReferralToSpecialistForm.types';

import { useReferralToSpecialistConfirmAndPrintCommand } from './ReferralToSpecialistSendAction.hooks';
import { mapReferralToSpecialistFormBeanToReferralCommandDetails } from './ReferralToSpecialistSendAction.utils';

type ReferralToSpecialistSendActionProps = {
   readonly handleSubmit: ReferralToSpecialistFormController['handleSubmit'];
   readonly trigger: ReferralToSpecialistFormController['trigger'];
   readonly onSuccess: VoidFunction;
   readonly visitId: number;
   readonly patientId: number;
};

// eslint-disable-next-line max-lines-per-function
export const ElectronicReferralToSpecialistSendAction = (props: ReferralToSpecialistSendActionProps): JSX.Element => {
   const [referralVerificationErrors, setReferralVerificationErrors] = useState<string[] | undefined>();
   const { openAccountAuthorizationModal, renderAccountAuthorizationnModal } = useAccountAuthorizationModal();
   const push = usePushBack();

   const handleReferralVerificationErrors = useCallback((fault?: ReferralVerificationFault) => {
      setReferralVerificationErrors(fault ? getReferralVerificationResultErrorList(fault) : []);
   }, []);

   const { executeAsync: removeDraft } = useDraftRemoveByTokenCqrsCommand();
   const removeElectronicReferralToSpecialistDraft = useCallback(
      async () => removeDraft({ token: getReferralDraftToken('electronicReferralToSpecialist', props.visitId) }),
      [props.visitId, removeDraft]
   );

   const { executeAsync: sendReferralToSpecialist } = useCqrsCommand<
      ElectronicReferralApprovalResult,
      ReferralToSpecialistFormBean,
      | InvalidPrivateKeyFault
      | NoValidCertificateFoundFault
      | PrivateKeyNotDecodedFault
      | ReferralVerificationFault
      | MissingHealthcareProviderActiveP1CertificatesFault
      | P1HandshakeFault
   >(
      (parameters) => {
         const command = new VisitReferralToSpecialistApproveCommand();
         command.visitId = props.visitId;
         command.referralDetails = {
            ...mapReferralToSpecialistFormBeanToReferralCommandDetails(parameters, true),
            icd9ProceduresCodes: parameters.icd9Procedures.map(({ code }) => code),
         };
         return command;
      },
      {
         onSuccess: async (result) => {
            dispatchElectronicReferralCodeSnackbarPositiveEvent(result.code);
            props.onSuccess();
            await removeElectronicReferralToSpecialistDraft();
            push(
               visitPaths.getVisitInterviewPath({
                  params: {
                     visitId: props.visitId,
                  },
               })
            );
         },
         onFault: {
            ...useCertificateFaultHandlerMap({
               onAuthenticationRequired: openAccountAuthorizationModal,
            }),
            'egabinet-referral-frontend-api:ReferralVerificationFault': handleReferralVerificationErrors,
            'egabinet-p1-frontend-api:MissingHealthcareProviderActiveP1CertificatesFault': () =>
               dispatchSnackbarNegativeEvent(referralTranslationKeys.missingHealthProviderCertificates),
            'egabinet-referral-frontend-api:P1HandshakeFault': () => {
               dispatchSnackbarNegativeEvent(referralTranslationKeys.certificateErrorOccurred);
            },
         },
      }
   );
   const { executeAsync: confirmAndPrintReferralToSpecialist } = useReferralToSpecialistConfirmAndPrintCommand(
      props.visitId,
      () => {
         props.onSuccess();
         removeElectronicReferralToSpecialistDraft();
      }
   );
   return (
      <>
         <ValidateFormEventInterceptor trigger={props.trigger}>
            <DataSyncWaitingEventInterceptor waitUntil={useDataSyncing()}>
               {(waiting) => (
                  <PatientNfzDepartmentUpdateShowModalEventInterceptor
                     patientId={props.patientId}
                     modalLabel={referralTranslationKeys.missingPatientNFZInfo}
                  >
                     <ActionBar.PrimaryButton
                        variant={ButtonVariant.PRIMARY}
                        onClick={props.handleSubmit(sendReferralToSpecialist)}
                        label={referralTranslationKeys.sendElectronicReferral}
                        testId={'sendReferral'}
                        disabled={waiting}
                        blockingOperation={referralTranslationKeys.submittingElectronicReferral}
                     />
                  </PatientNfzDepartmentUpdateShowModalEventInterceptor>
               )}
            </DataSyncWaitingEventInterceptor>
         </ValidateFormEventInterceptor>
         {renderAccountAuthorizationnModal({
            onAuthorization: props.handleSubmit(sendReferralToSpecialist),
         })}
         {referralVerificationErrors ? (
            <ElectronicReferralFailureModal
               onConfirm={props.handleSubmit(confirmAndPrintReferralToSpecialist)}
               onCancel={() => setReferralVerificationErrors(undefined)}
               errors={referralVerificationErrors}
            />
         ) : null}
      </>
   );
};
